import { IOffer, IPhoto } from "types/IOffer";
import { DPE, GES } from "potassium-react-dpe-generator";
import PropertyDetail from "components/PropertyDetail";

import {
  Land,
  Dimension,
  Plan,
  Bed,
  Elevator,
  Bathtub,
  Shower,
  Garage,
} from "helpers/icons";

/**
 * Returns a photo list with clean path (starting with http)
 * @param photos // photo array to clean
 * @param minCount // number of items that should be returned
 *  */
export const getCleanPhotoList = (photos: IPhoto[], minCount?: number) => {
  if (photos.length === 0) {
    return [];
  }
  let result: IPhoto[] = [...photos];

  if (minCount) {
    while (result.length < minCount) {
      photos.forEach((photo) => {
        // if (result.length < minCount) {
        result.push(photo);
        // } else {
        //   return;
        // }
      });
    }
  }
  return result;
};

/**
 * Returns label to be displayed
 * @param offer // IOffer
 *  */
export const getBannerText = (offer: IOffer) => {
  let result = "";
  const status = offer.status.toLowerCase();
  if (offer.exclusiveMandate === true) {
    result += "Exclusivité";
    if (status !== null && status !== "actif") {
      result += `<br />${offer.status}`;
    }
  } else if (status !== null && status !== "actif") {
    result += offer.status;
  } else {
    return null;
  }
  return result;
};

/**
 * Returns label to be displayed
 * @param offer // IOffer
 *  */
export const getPriceDetails = (offer: IOffer) => {
  let result = "";

  let diagnosticDate =
    offer.diagnosticDate && offer.diagnosticDate !== "00/00/0000"
      ? ` - Date de réalisation du diagnostic : ${offer.diagnosticDate}`
      : "";

  let mandataire = offer.contactToDisplay.trim() + ": " + offer.phoneToDisplay;

  result = `
  Cette annonce vous est proposée par IMKO DK - 894 990 910,
  Annonce rédigée et publiée par (${mandataire}) - agent mandataire ou salarié.e.
  ${diagnosticDate}`;

  let feesPayableBy = "";
  switch (offer.feesPayableBy) {
    case 1:
      feesPayableBy = "de l'acheteur";
      break;
    case 2:
      feesPayableBy = "du vendeur";
      break;
    case 3:
      feesPayableBy = "de l'acheteur et du vendeur";
      break;
  }

  if (feesPayableBy !== "") {
    const fees = Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(offer.price - offer.priceExcludingFees);

    result += `
    Le prix indiqué comprend les honoraires à la charge ${feesPayableBy} : ${offer.agencyFees}% TTC du prix du bien hors honoraires.
    Prix des honoraires : ${fees}"
    `;
  }

  return result;
};

/**
 * Returns DPE / GES images to be displayed
 * @param offer // IOffer
 *  */
export const getDpeGes = (offer: IOffer, width: number, height: number) => {
  if (!offer) {
    return null;
  }

  return (
    <>
      {/* {offer.dpeClassification === "D" && ( */}
      <DPE value={offer.dpe} height={height} width={width} />
      {/* )} */}
      {/* {offer.dpeClassification === "VI" && "DPE Vierge"}
      {offer.dpeClassification === "NS" && "DPE Non Soumis"} */}
      &nbsp;
      {/* {offer.gesClassification === "E" && ( */}
      <GES value={offer.ges} height={height} width={width} />
      {/* )}
       {offer.gesClassification === "VI" && "GES Vierge"}
       {offer.gesClassification === "NS" && "GES Non Soumis"} */}
    </>
  );
};

/**
 * Returns property details to be displayed
 * @param offer // IOffer
 *  */
export const getPropertyDetails = (
  offer: IOffer,
  size: number,
  maxCount: number
) => {
  let result: any = [];
  if (!offer) {
    return result;
  }

  if (offer.livingArea > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-SURFACE"}
        icon={Dimension}
        title={"SURFACE"}
        description={`${offer.livingArea}m²`}
        width={size}
      />
    );
  }

  if (offer.landArea > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-TERRAIN"}
        icon={Land}
        title={"TERRAIN"}
        description={`${offer.landArea}m²`}
        width={size}
      />
    );
  }
  if (offer.numberOfRoom > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-PIECES"}
        icon={Plan}
        title={"PIECE(S)"}
        description={offer.numberOfRoom.toString()}
        width={size}
      />
    );
  }
  if (offer.numberOfBedroom > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-CHAMBRES"}
        icon={Bed}
        title={"CHAMBRE(S)"}
        description={offer.numberOfBedroom.toString()}
        width={size}
      />
    );
  }
  if (offer.floor > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-ASCENSEUR"}
        icon={Elevator}
        title={"ASCENSEUR"}
        description={offer.elevator ? "OUI" : "NON"}
        width={size}
      />
    );
  }
  if (offer.numberOfBathroom > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-SALLESDEBAIN"}
        icon={Bathtub}
        title={"SALLE(S) DE BAIN"}
        description={offer.numberOfBathroom.toString()}
        width={size}
      />
    );
  }
  if (offer.numberOfShowerRoom > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-SALLESDEAU"}
        icon={Shower}
        title={"SALLE(S) D'EAU"}
        description={offer.numberOfShowerRoom.toString()}
        width={size}
      />
    );
  }
  if (offer.numberOfBox > 0) {
    result.push(
      <PropertyDetail
        key={"propertyDetail-GARAGE"}
        icon={Garage}
        title={"GARAGE"}
        description={offer.numberOfBox.toString()}
        width={size}
      />
    );
  }

  return result.slice(0, maxCount);
};

/**
 * Returns description to be displayed
 * @param offer // IOffer
 *  */
export const getDescription = (offer: IOffer) => {
  return offer.description;
};

/**
 * Returns description to be displayed
 * @param offer // IOffer
 *  */
export const getContactPhone = (offer: IOffer) => {
  let result = "";

  if (offer.phoneToDisplay !== null && offer.phoneToDisplay !== "") {
    result += "Téléphone du contact agence: " + offer.phoneToDisplay;
  }

  return result;
};
